import { msg } from "@lingui/core/macro";
import { createMemo, createSignal, For, Show } from "solid-js";
import { useSearch } from "@tanstack/solid-router";
import { useNavigate } from "@/lib/u-router";
import { Ban, RotateCcw, Search, ShieldCheck, Trash2, Users } from "lucide-solid";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { TextField } from "@/components/ui/text-field";
import { Select } from "@/components/ui/select";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { Dialog, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/text-field";
import { showToast } from "@/components/ui/toast";
import { api, createApiMutation, createApiQuery } from "@/lib/api";
import {
  adminQueryKeys,
  type AdminUserDTO,
  ASSIGNABLE_ROLES,
  type components,
  filterAdminUsers,
} from "@mealplan/shared";
import { useI18n } from "@/providers/i18n-provider";
import { useAdminCapabilities } from "../use-admin-capabilities";

type ErrorModel = components["schemas"]["ErrorModel"];
type UserListResponse = components["schemas"]["AdminUserListResponse"];

/** Action the URL is asking us to perform on a user (`?user=<id>&action=ban`). */
type UserAction = "ban" | "remove";

export function UserManager() {
  const { t } = useI18n();
  const { can } = useAdminCapabilities();
  const search = useSearch({ from: "/u/$u/admin/users" });
  const navigate = useNavigate();

  const [banReason, setBanReason] = createSignal("");

  const usersQuery = createApiQuery<UserListResponse, ErrorModel>(
    () => adminQueryKeys.users(),
    async () => api.GET("/api/admin/users", {}),
    () => ({ staleTime: 15_000 }),
  );

  const users = createMemo<AdminUserDTO[]>(() => usersQuery.data?.data ?? []);

  const setRoleMutation = createApiMutation(async (vars: { user_id: string; role: string }) =>
    api.POST("/api/admin/set-role", { body: vars }),
  );
  const banMutation = createApiMutation(async (vars: { user_id: string; reason?: string }) =>
    api.POST("/api/admin/ban-user", { body: vars }),
  );
  const unbanMutation = createApiMutation(async (vars: { userId: string }) =>
    api.POST("/api/admin/unban-user", { body: vars }),
  );
  const revokeMutation = createApiMutation(async (vars: { userId: string }) =>
    api.POST("/api/admin/revoke-sessions", { body: vars }),
  );
  const removeMutation = createApiMutation(async (vars: { userId: string }) =>
    api.POST("/api/admin/remove-user", { body: vars }),
  );

  const result = createMemo(() =>
    filterAdminUsers(users(), {
      q: search().q,
      role: search().role,
      page: search().page,
    }),
  );

  /** Patch the URL search params (keeps list state addressable, #15b). */
  const patchSearch = (patch: Record<string, unknown>) => {
    void navigate({
      to: ".",
      search: (prev) => {
        const next = { ...prev, ...patch };
        for (const key of Object.keys(next)) {
          if (next[key] === undefined || next[key] === "") {
            delete next[key];
          }
        }
        return next;
      },
      replace: true,
    });
  };

  const dialogUser = createMemo<AdminUserDTO | undefined>(() => {
    const id = search().user;
    return id ? users().find((u) => u.id === id) : undefined;
  });
  const dialogAction = (): UserAction | undefined => search().action;

  const closeDialog = () => {
    setBanReason("");
    patchSearch({ user: undefined, action: undefined });
  };

  const openDialog = (userId: string, action: UserAction) => {
    patchSearch({ user: userId, action });
  };

  const afterMutation = (message: string) => {
    void usersQuery.refetch();
    showToast(message, "success");
  };

  // El `Select` propio del clon toma `readonly string[]` (no objetos value/label),
  // así que las opciones de rol son los valores de rol tal cual.
  const roleOptions = createMemo<readonly string[]>(() => ASSIGNABLE_ROLES);

  const handleBan = () => {
    const user = dialogUser();
    if (!user) {
      closeDialog();
      return;
    }
    banMutation.mutate(
      { user_id: user.id, reason: banReason().trim() || undefined },
      {
        onSuccess: () => {
          afterMutation(t(msg`User banned`));
          closeDialog();
        },
      },
    );
  };

  const handleRemove = () => {
    const user = dialogUser();
    if (!user) {
      closeDialog();
      return;
    }
    removeMutation.mutate(
      { userId: user.id },
      {
        onSuccess: () => {
          afterMutation(t(msg`User removed`));
          closeDialog();
        },
      },
    );
  };

  return (
    <div class="space-y-6">
      <div class="flex items-center gap-3">
        <Users class="h-8 w-8 text-primary" />
        <div>
          <h2 class="text-3xl font-bold tracking-tight">{t(msg`Users`)}</h2>
          <p class="text-sm text-muted-foreground">
            {(() => {
              const { total } = result();
              return t(msg`${total} users match`);
            })()}
          </p>
        </div>
      </div>

      <Card class="bg-primary/5 border-primary/10">
        <CardContent class="p-4 flex flex-col gap-3 sm:flex-row sm:items-center">
          <div class="relative flex-1">
            <Search class="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <TextField
              placeholder={t(msg`Search by email or name…`)}
              class="pl-9"
              value={search().q ?? ""}
              onInput={(e) => {
                patchSearch({ q: e.currentTarget.value, page: undefined });
              }}
            />
          </div>
          <div class="w-full sm:w-48">
            <Select
              aria-label={t(msg`Filter by role`)}
              placeholder={t(msg`All roles`)}
              value={search().role ?? ""}
              options={["", ...roleOptions()]}
              onChange={(v) => {
                patchSearch({ role: v ? v : undefined, page: undefined });
              }}
            />
          </div>
        </CardContent>
      </Card>

      <Show
        when={!usersQuery.isLoading}
        fallback={
          <div class="space-y-3">
            <For each={Array.from({ length: 6 }, () => 0)}>
              {() => <Skeleton class="h-20 w-full rounded-xl" />}
            </For>
          </div>
        }
      >
        <div class="space-y-3">
          <For
            each={result().rows}
            fallback={
              <Card>
                <CardContent class="p-8 text-center text-sm text-muted-foreground">
                  {t(msg`No users found.`)}
                </CardContent>
              </Card>
            }
          >
            {(user) => (
              <Card>
                <CardContent class="p-4 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                  <div class="min-w-0 flex items-center gap-3">
                    <div class="h-10 w-10 shrink-0 rounded-full bg-accent text-accent-foreground flex items-center justify-center font-bold uppercase">
                      {(user.name || user.email).charAt(0)}
                    </div>
                    <div class="min-w-0">
                      <div class="font-bold truncate flex items-center gap-2">
                        {user.name || t(msg`(no name)`)}
                        <Show when={user.banned}>
                          <Badge variant="destructive">{t(msg`Banned`)}</Badge>
                        </Show>
                      </div>
                      <div class="text-xs text-muted-foreground truncate">{user.email}</div>
                    </div>
                  </div>

                  <div class="flex flex-wrap items-center gap-2">
                    <Show
                      when={can("users.setRole")}
                      fallback={
                        <Badge variant="secondary" class="capitalize">
                          {user.role}
                        </Badge>
                      }
                    >
                      <div class="w-36">
                        <Select
                          aria-label={t(msg`Role`)}
                          value={user.role}
                          options={roleOptions()}
                          onChange={(role) => {
                            if (!role) {
                              return;
                            }
                            setRoleMutation.mutate(
                              { user_id: user.id, role },
                              { onSuccess: () => afterMutation(t(msg`Role updated`)) },
                            );
                          }}
                        />
                      </div>
                    </Show>

                    <Show when={can("users.ban")}>
                      <Show
                        when={user.banned}
                        fallback={
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openDialog(user.id, "ban")}
                          >
                            <Ban class="mr-1.5 h-4 w-4" />
                            {t(msg`Ban`)}
                          </Button>
                        }
                      >
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            unbanMutation.mutate(
                              { userId: user.id },
                              { onSuccess: () => afterMutation(t(msg`User unbanned`)) },
                            );
                          }}
                        >
                          <ShieldCheck class="mr-1.5 h-4 w-4" />
                          {t(msg`Unban`)}
                        </Button>
                      </Show>
                    </Show>

                    <Show when={can("users.revokeSessions")}>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          revokeMutation.mutate(
                            { userId: user.id },
                            { onSuccess: () => afterMutation(t(msg`Sessions revoked`)) },
                          );
                        }}
                      >
                        <RotateCcw class="mr-1.5 h-4 w-4" />
                        {t(msg`Revoke sessions`)}
                      </Button>
                    </Show>

                    <Show when={can("users.remove")}>
                      <Button
                        variant="ghost"
                        size="sm"
                        class="text-destructive hover:text-destructive"
                        onClick={() => openDialog(user.id, "remove")}
                      >
                        <Trash2 class="mr-1.5 h-4 w-4" />
                        {t(msg`Remove`)}
                      </Button>
                    </Show>
                  </div>
                </CardContent>
              </Card>
            )}
          </For>
        </div>

        <Show when={result().totalPages > 1}>
          <div class="flex items-center justify-center gap-4 pt-2">
            <Button
              variant="ghost"
              size="sm"
              disabled={result().page <= 1}
              onClick={() => patchSearch({ page: result().page - 1 })}
            >
              {t(msg`Previous`)}
            </Button>
            <span class="text-sm text-muted-foreground">
              {(() => {
                const { page } = result();
                const { totalPages } = result();
                return t(msg`Page ${page} of ${totalPages}`);
              })()}
            </span>
            <Button
              variant="ghost"
              size="sm"
              disabled={result().page >= result().totalPages}
              onClick={() => patchSearch({ page: result().page + 1 })}
            >
              {t(msg`Next`)}
            </Button>
          </div>
        </Show>
      </Show>

      <Dialog
        open={Boolean(dialogUser()) && dialogAction() === "ban"}
        onOpenChange={(open) => {
          if (!open) {
            closeDialog();
          }
        }}
        contentClass="max-w-[420px] p-6"
      >
        <DialogTitle class="text-xl font-bold mb-2">{t(msg`Ban user`)}</DialogTitle>
        <DialogDescription class="text-sm text-muted-foreground mb-4">
          {t(msg`The user will be unable to sign in. You can provide an optional reason.`)}
        </DialogDescription>
        <div class="space-y-2">
          <Label for="ban-reason">{t(msg`Reason (optional)`)}</Label>
          <TextField
            id="ban-reason"
            value={banReason()}
            onInput={(e) => {
              setBanReason(e.currentTarget.value);
            }}
          />
        </div>
        <div class="flex justify-end gap-3 mt-6">
          <Button variant="ghost" onClick={closeDialog}>
            {t(msg`Cancel`)}
          </Button>
          <Button variant="destructive" disabled={banMutation.isPending} onClick={handleBan}>
            {t(msg`Ban user`)}
          </Button>
        </div>
      </Dialog>

      <ConfirmDialog
        open={Boolean(dialogUser()) && dialogAction() === "remove"}
        onOpenChange={(open) => {
          if (!open) {
            closeDialog();
          }
        }}
        title={t(msg`Remove user (GDPR)`)}
        description={t(
          msg`This permanently deletes the user and all their data. This cannot be undone.`,
        )}
        confirmLabel={t(msg`Permanently remove`)}
        variant="destructive"
        isLoading={removeMutation.isPending}
        onConfirm={handleRemove}
        onCancel={closeDialog}
      />
    </div>
  );
}
